/**
 * Dashboard by vulnerability (DEV-133) — areas, search and order (pure, React-free).
 *
 * The server delivers every CVE with its products; each product names the
 * areas it stands in (see cve_dashboard_service.py). This module folds them
 * into the sections of the page: a CVE appears in every area it has products
 * in and shows only those there. In „Zu bewerten“ a product shows only its
 * versions without a confirmed verdict.
 */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

import type { CveArea, CveDashboardEntry, CveDashboardProduct } from '../../lib/types';

/** Section titles and empty states; translated at render with `i18n._()`. */
export const CVE_AREAS: ReadonlyArray<{ area: CveArea; title: MessageDescriptor; empty: MessageDescriptor | null }> = [
  { area: 'assess', title: msg`Zu bewerten`, empty: msg`Keine offenen Bewertungen` },
  { area: 'in_progress', title: msg`In Bearbeitung`, empty: msg`Kein offener Schwachstellenfall` },
  { area: 'done', title: msg`Abgeschlossen`, empty: msg`Noch kein Schwachstellenfall abgeschlossen` },
  // Shown only when it has entries.
  { area: 'none', title: msg`Ohne Handlungsbedarf`, empty: null },
];

export interface CveGroup {
  entry: CveDashboardEntry;
  /** The products of this area, in „Zu bewerten“ with their unconfirmed versions only. */
  products: CveDashboardProduct[];
  /** An unreported deadline of one of the products has passed. */
  overdue: boolean;
  /** The earliest open deadline of the products, or null. */
  nextDeadline: string | null;
}

/** Lower-cased, space-separated search tokens; every one has to match. */
export function cveSearchTokens(search: string): string[] {
  return search.toLowerCase().split(/\s+/).filter(Boolean);
}

function productText(entry: CveDashboardEntry, product: CveDashboardProduct, components: readonly string[]): string {
  return [
    entry.cve, product.product_name, ...components,
    ...product.versions.map((version) => version.version_nr ?? ''),
    ...product.customers.flatMap((customer) => [
      customer.name ?? '', ...customer.instances.map((instance) => instance.serial ?? ''),
    ]),
  ].join(' ').toLowerCase();
}

/** Free text over CVE, product, version, customer, serial number and component. */
export function matchesProduct(
  entry: CveDashboardEntry, product: CveDashboardProduct, tokens: readonly string[], components: readonly string[] = [],
): boolean {
  if (tokens.length === 0) return true;
  const text = productText(entry, product, components);
  return tokens.every((token) => text.includes(token));
}

/** In „Zu bewerten“ only the unconfirmed versions and the deliveries running them. */
function inArea(product: CveDashboardProduct, area: CveArea): CveDashboardProduct {
  if (area !== 'assess') return product;
  const versions = product.versions.filter((version) => !version.confirmed);
  const numbers = new Set(versions.map((version) => version.version_nr));
  const customers = product.customers
    .map((customer) => ({ ...customer, instances: customer.instances.filter((i) => numbers.has(i.version_nr)) }))
    .filter((customer) => customer.instances.length > 0);
  return { ...product, versions, customers };
}

function compareGroups(a: CveGroup, b: CveGroup): number {
  if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
  if (a.nextDeadline !== b.nextDeadline) {
    if (a.nextDeadline === null) return 1;
    if (b.nextDeadline === null) return -1;
    return a.nextDeadline.localeCompare(b.nextDeadline);
  }
  const hitA = a.entry.first_hit_at ?? '9999';
  const hitB = b.entry.first_hit_at ?? '9999';
  return hitA.localeCompare(hitB) || a.entry.cve.localeCompare(b.entry.cve);
}

/**
 * The CVEs of one area with their products of that area. Order: overdue
 * deadlines first, then the next open deadline, then the oldest open KEV hit,
 * then the CVE id.
 */
export function areaGroups(
  entries: readonly CveDashboardEntry[], area: CveArea, tokens: readonly string[] = [],
  componentsByCve: ReadonlyMap<string, readonly string[]> = new Map(),
): CveGroup[] {
  const groups: CveGroup[] = [];
  for (const entry of entries) {
    const components = componentsByCve.get(entry.cve) ?? [];
    const products = entry.products
      .filter((product) => product.areas.includes(area) && matchesProduct(entry, product, tokens, components))
      .map((product) => inArea(product, area));
    if (products.length === 0) continue;
    const deadlines = products.map((product) => product.case?.next_deadline).filter((due): due is string => !!due);
    groups.push({
      entry,
      products,
      overdue: products.some((product) => product.overdue),
      nextDeadline: deadlines.sort()[0] ?? null,
    });
  }
  return groups.sort(compareGroups);
}

/** Customers and deliveries of the products shown in a group. */
export function groupCounts(group: CveGroup): { customers: number; deliveries: number } {
  const customers = new Set<string>();
  let deliveries = 0;
  for (const product of group.products) {
    for (const customer of product.customers) {
      customers.add(String(customer.customer_id ?? customer.name));
      deliveries += customer.instances.length;
    }
  }
  return { customers: customers.size, deliveries };
}
